import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAtom, useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo, useState } from 'react';
import { PermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import {
  captableVersionsByCompanyStateJ,
  companyCaptablesByIdStateJ,
  selectedCaptableStateJ,
} from '../../../../services/state/CapTableDataStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { MenuButton } from '../../../Finance2/common-grid-defs/MenuButton';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { isUserCreated } from '../captable-utils';
import { useEditCaptable } from '../CapTableActions';
import {
  CaptableSummaryGridConfigDialog,
  showCapTableSummaryGridConfigAtom,
} from './CaptableSummaryGridConfig';
import { DeleteCaptableDialog } from './DeleteCaptableDialog';

interface ICaptableActionsMenuProps {
  showGridViewOptions: boolean;
}

export function CaptableActionsMenu({ showGridViewOptions }: ICaptableActionsMenuProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const editCaptable = useEditCaptable();
  const currentCaptable = useAtomValue(selectedCaptableStateJ(companyId));
  const captablesById = useAtomValue(companyCaptablesByIdStateJ(companyId));
  const versionGroups = useAtomValue(captableVersionsByCompanyStateJ(companyId));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { showCapTableSummaryGridConfig } = useFlags<MaggieFeatureFlags>();
  const [showCapTableViewConfig, setShowCapTableViewConfig] = useAtom(showCapTableSummaryGridConfigAtom);
  // Modify loads the form from the current-per-source map (`useSyncCaptableFormFromPath`), so a
  // selection absent from it would open a blank form and save an empty payload.
  const isInCurrentList = currentCaptable != null && captablesById.has(currentCaptable.id);
  // Delete needs only an id, so it gates on the timeline instead. The current-per-source list keeps
  // a single user-created row, while the versions endpoint groups on raw (source, type) — a user
  // timeline that isn't that one row is absent from the list while still being its own current
  // version. Fall back to the list when timelines aren't loaded (flag off).
  const isCurrentVersion = useMemo(() => {
    if (currentCaptable == null) {
      return false;
    }
    if (!versionGroups) {
      return isInCurrentList;
    }
    const selectedVersion = versionGroups
      .flatMap((group) => group.versions)
      .find((version) => version.id === currentCaptable.id);
    return selectedVersion?.isCurrent ?? isInCurrentList;
  }, [currentCaptable, versionGroups, isInCurrentList]);

  const hasWritePermission = PermissionService.get().hasPermission(PermissionKey.canWriteCaptable);
  const isUserCaptable = currentCaptable != null && isUserCreated(currentCaptable);
  const canModifyCaptable = hasWritePermission && isUserCaptable && isInCurrentList;
  const canDeleteCaptable = hasWritePermission && isUserCaptable && isCurrentVersion;

  const onCloseCapTableViewConfig = useCallback(() => {
    setShowCapTableViewConfig(false);
  }, [setShowCapTableViewConfig]);

  const menuItems = useMemo(() => {
    if (!currentCaptable) {
      return [];
    }
    if (!canModifyCaptable && !canDeleteCaptable && !(showCapTableSummaryGridConfig && showGridViewOptions)) {
      return [];
    }
    const res = [];
    if (canModifyCaptable) {
      res.push({
        name: 'Modify Cap Table',
        icon: <EditOutlinedIcon />,
        onClick: () => {
          editCaptable(currentCaptable);
        },
      });
    }
    if (canDeleteCaptable) {
      res.push({
        name: 'Delete Cap Table',
        icon: <DeleteOutlineIcon />,
        onClick: () => {
          setShowDeleteDialog(true);
        },
      });
    }
    if (showCapTableSummaryGridConfig && showGridViewOptions) {
      res.push({
        name: 'Grid View Options',
        icon: <SettingsIcon />,
        onClick: () => {
          setShowCapTableViewConfig(true);
        },
      });
    }
    return res;
  }, [
    canDeleteCaptable,
    canModifyCaptable,
    currentCaptable,
    editCaptable,
    setShowCapTableViewConfig,
    showCapTableSummaryGridConfig,
    showGridViewOptions,
  ]);

  if (menuItems.length === 0) {
    return null;
  }
  return (
    <>
      <CaptableSummaryGridConfigDialog open={showCapTableViewConfig} onClose={onCloseCapTableViewConfig} />
      {showDeleteDialog && <DeleteCaptableDialog open={showDeleteDialog} setOpen={setShowDeleteDialog} />}
      <MenuButton menuItems={menuItems} label='Cap table actions' />
    </>
  );
}
